import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UploadCloud, FileText, Loader2, AlertTriangle, X } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { canUploadSourceForm } from '../../lib/roles';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useActiveEquipmentTypes } from '../../hooks/usePreuseTemplates';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PREUSE_TARGET, preuseExtractionRefusalSentence } from '../../lib/preuse-extraction';
import { DH_INPUT, DhEmpty, DhField, DhPanel } from '../downhole/DownholeBits';
import { NoEquipmentTypesNotice, PREUSE_LIST } from '../equipment/PreuseTemplatesScreen';

/**
 * Every type the rasterizer accepts, from `form-rasterize.service.ts`. Kept as
 * one list so the `accept` attribute and the human-readable line below cannot
 * drift apart and promise different things.
 */
const ACCEPTED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
].join(',');

const ACCEPTED_LABEL = 'PDF, PNG, JPG, HEIC, WebP or TIFF';

/** Statuses that mean the run is still moving. Anything else is terminal. */
const IN_FLIGHT = new Set(['queued', 'running']);

/**
 * A run that never settles must not spin forever. The rasterize step is
 * synchronous and already done by the time we poll; this budget covers the
 * queued extraction only. When it runs out the screen says so and offers the
 * run id — an honest dead end beats a spinner that means nothing.
 */
const POLL_TIMEOUT_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = 2000;

type Phase = 'idle' | 'uploading' | 'extracting' | 'failed';

/** The extraction run row. snake_case — this endpoint returns the raw DB row. */
interface ExtractionRun {
  id: string;
  status: string;
  error_message: string | null;
  page_count: number | null;
  original_filename: string | null;
}

/**
 * Upload a form your company already uses, and let FieldIQ rebuild it.
 *
 * WHY THERE IS NO NAME OR CATEGORY FIELD HERE
 * -------------------------------------------
 * `POST /forms/source-documents` accepts exactly two things: `file` and
 * `ownership_attested`. It has nowhere to put a name or a category — those are
 * required by `POST /forms/extractions/:id/approve`, at the END of the review,
 * where the reviewer picks them against the schema the model actually proposed.
 *
 * A category select here would post nothing and store nothing while looking
 * like it had. That is the silent-wrong-value failure this repo keeps finding,
 * so the screen tells the truth about the data model instead.
 */
export default function FormReplicatorUploadScreen({
  target,
}: {
  /**
   * `'preuse_template'` (api#1210): read the document as an equipment PRE-USE
   * checklist for one equipment type, reached from the Pre-use templates
   * register. Absent: the ordinary safety-form replicator, unchanged.
   */
  target?: typeof PREUSE_TARGET;
} = {}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const preuse = target === PREUSE_TARGET;
  const { user } = useAuth();
  // Fetched only in pre-use mode: the ordinary path makes no new request.
  const types = useActiveEquipmentTypes(preuse);
  const [typeId, setTypeId] = useState('');

  const [file, setFile] = useState<File | null>(null);
  const [attested, setAttested] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);

  const busy = phase === 'uploading' || phase === 'extracting';
  // A pre-use run is refused without an equipment type, so it is asked for first.
  const canSubmit = !!file && attested && !busy && (!preuse || !!typeId);

  const pickFile = (next: File | null) => {
    setFile(next);
    setErrorText(null);
    if (phase === 'failed') setPhase('idle');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) pickFile(dropped);
  };

  /**
   * Poll until the run leaves queued/running.
   *
   * Every failure mode here has to be loud. A run that fails, a run that never
   * settles, and a run that succeeds must be three visibly different outcomes —
   * not one spinner that eventually stops.
   */
  const pollRun = async (runId: string): Promise<ExtractionRun> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const res = await api.forms.extraction(runId);
      const run = unwrap<ExtractionRun>(res.data);

      // `data: null` on a 200 is a real answer, and reading the envelope as a
      // run would leave `status` undefined and exit the loop as if it settled.
      if (!run) throw new Error('The server returned no extraction run.');

      if (!IN_FLIGHT.has(run.status)) return run;

      setStatusText(run.status === 'queued' ? 'Queued…' : 'Reading the form…');

      if (Date.now() > deadline) {
        throw new Error(
          `This is taking longer than expected. The run is still ${run.status}. Reference: ${runId}`,
        );
      }

      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  const handleSubmit = async () => {
    if (!file || !attested) return;
    if (preuse && !typeId) return;

    setPhase('uploading');
    setErrorText(null);
    setStatusText('Uploading and splitting pages…');

    try {
      const docRes = await api.forms.createSourceDocument(file);
      const doc = unwrap<{ id: string; pageCount: number }>(docRes.data);
      if (!doc?.id) throw new Error('The upload succeeded but returned no document id.');

      setPhase('extracting');
      setStatusText('Queuing extraction…');

      const runRes = preuse
        ? await api.forms.createExtraction(doc.id, { target: PREUSE_TARGET, equipment_type_id: typeId })
        : await api.forms.createExtraction(doc.id);
      const run = unwrap<{ id: string; status: string }>(runRes.data);
      if (!run?.id) throw new Error('The extraction was accepted but returned no run id.');

      const settled = await pollRun(run.id);

      if (settled.status === 'failed') {
        // The API's own message, verbatim — it knows why this failed and we do
        // not. Only fall back to our own copy when it sent none.
        throw new Error(settled.error_message || 'The extraction failed.');
      }

      if (preuse) {
        toast.success('Checklist read — ready for review');
        navigate(`${PREUSE_LIST}/extractions/${settled.id}`, {
          state: { filename: file.name, status: settled.status },
        });
        return;
      }
      toast.success('Form read — ready for review');
      navigate(`/settings/safety/forms/extractions/${settled.id}`, {
        state: { filename: file.name, status: settled.status },
      });
    } catch (e: any) {
      setPhase('failed');
      setStatusText(null);
      // A pre-use refusal carries a `code` (equipment_type_inactive, …) that
      // reads better as a sentence; a thrown Error keeps its own message.
      if (preuse && e?.response) {
        setErrorText(preuseExtractionRefusalSentence(e, 'Something went wrong with this upload.'));
        return;
      }
      // getApiErrorMessage prefers the API's own error text, which is what
      // carries the page-limit and unsupported-type rejections.
      setErrorText(getApiErrorMessage(e, e?.message || 'Something went wrong with this upload.'));
    }
  };

  /** Everything below the intro — the same in both modes. */
  const uploadBody = (
    <>

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragging
              ? 'border-fieldiq-accent bg-fieldiq-accent/10'
              : 'border-fieldiq-border bg-fieldiq-card/40'
          } ${busy ? 'opacity-60' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME}
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />

          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={20} className="text-fieldiq-accent shrink-0" />
              <span className="text-sm text-white break-all">{file.name}</span>
              {!busy && (
                <button
                  onClick={() => pickFile(null)}
                  aria-label="Remove file"
                  className="p-1 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 shrink-0"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ) : (
            <>
              <UploadCloud size={40} className="mx-auto text-fieldiq-muted mb-3" />
              <p className="text-sm text-white font-medium">Drag a form here</p>
              <p className="text-xs text-muted-foreground mt-1">
                {ACCEPTED_LABEL} — up to 6 pages
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="mt-4 px-4 py-2 bg-fieldiq-card border border-fieldiq-border text-white rounded-lg text-sm font-medium hover:border-fieldiq-accent transition-colors disabled:opacity-50"
              >
                Choose a file
              </button>
            </>
          )}
        </div>

        {/* Attestation — a stored fact on the row, not just a gate */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            disabled={busy}
            className="mt-0.5 accent-fieldiq-accent w-4 h-4 shrink-0"
          />
          <span className="text-sm text-fieldiq-text">
            I confirm this form belongs to my company and we have the right to digitize it.
          </span>
        </label>

        {/* Failure — the API's own words, and a way to try again */}
        {phase === 'failed' && errorText && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-red-300 font-medium">Upload failed</p>
                <p className="text-sm text-red-200/90 mt-1 break-words">{errorText}</p>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="mt-3 px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        )}

        {busy && statusText && (
          <div className="flex items-center gap-2 text-sm text-fieldiq-text">
            <Loader2 size={16} className="animate-spin text-fieldiq-accent" />
            {statusText}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-5 py-2.5 bg-fieldiq-accent text-white rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? 'Working…' : 'Upload and read'}
          </button>
          {!attested && file && (
            <span className="text-xs text-muted-foreground">
              Confirm ownership above to continue.
            </span>
          )}
        </div>
    </>
  );

  if (preuse) {
    const typeFailure = queryLoadFailure(types);
    const typeList = types.data ?? [];
    return (
      <div data-testid="preuse-from-document-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PageHeaderBand
          eyebrow="Pre-use templates"
          title="Build from a document"
          onBack={() => navigate(PREUSE_LIST)}
          backLabel="Back to templates"
        />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px] space-y-4">
            {!canUploadSourceForm(user?.role) ? (
              <DhPanel eyebrow="Build from a document" title="Not available">
                <DhEmpty>Only an admin or office staff can build a pre-use template from a document.</DhEmpty>
              </DhPanel>
            ) : types.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading equipment types" />
              </div>
            ) : typeFailure ? (
              <DhPanel eyebrow="Build from a document" title="Equipment types">
                <LoadFailureNotice error={queryFailureError(types)} kind={typeFailure} subject="equipment types" />
              </DhPanel>
            ) : typeList.length === 0 ? (
              <DhPanel eyebrow="Build from a document" title="Add an equipment type first">
                <NoEquipmentTypesNotice />
              </DhPanel>
            ) : (
              <DhPanel eyebrow="AI form replicator" title="Read a pre-use checklist" testId="preuse-from-document">
                <div className="space-y-4 p-4">
                  <p className="text-sm text-fieldiq-text">
                    Upload the pre-use or walk-around checklist your crews already fill in on paper. FieldIQ reads it
                    and proposes the items, guessing which are critical. You review every item before anything is
                    created, and approving makes a <strong>draft</strong> template — nothing reaches a phone until an
                    admin publishes it.
                  </p>
                  <div className="max-w-xl">
                    <DhField label="Equipment type *" hint="The draft template will be for this type.">
                      <select
                        className={DH_INPUT}
                        value={typeId}
                        disabled={busy}
                        onChange={(e) => setTypeId(e.target.value)}
                      >
                        <option value="">Choose a type…</option>
                        {typeList.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </DhField>
                  </div>
                  {uploadBody}
                  {!typeId && file && attested && (
                    <p className="text-xs text-muted-foreground">Choose the equipment type above to continue.</p>
                  )}
                </div>
              </DhPanel>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      <div className="border-b border-border px-6 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/settings/safety/forms')}
            aria-label="Back"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-foreground">Replicate a Form</h1>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4 max-w-3xl">
        <p className="text-sm text-fieldiq-text">
          Upload a form your crews already fill in on paper. FieldIQ reads it and proposes a digital
          version for you to review. You'll name it and pick its category at the review step, once
          you can see what was actually read.
        </p>

        {uploadBody}
      </div>
    </div>
  );
}
